import BlockContent from 'components/block-content';
import Hero from 'components/hero';
import Layout from 'components/layout';
import Map, { MapMarker } from 'components/map';
import SEO from 'components/seo';
import { Box, Button, ButtonGroup, Stack, Text } from 'components/ui';
import { GetStaticProps, NextPage } from 'next';
import Link from 'next/link';
import { REVALIDATE_TIME } from 'settings/config';
import styled from 'styled-components';
import { breakpoints } from 'styles/theme';
import { __DEV__, getBoundsFromLocations } from 'utils/helpers';
import {
  SharedData,
  locationsQuery,
  locationsTextQuery,
  sharedDataQuery,
} from 'utils/queries';
import {
  formatAddress,
  formatAddressWeblink,
  usePreviewSubscription,
} from 'utils/sanity';
import { getClient } from 'utils/sanity-server';

interface LocationsPageProps {
  locationsData?: Sanity.Schema.Location[];
  sharedData?: SharedData;
  locationsTextData?: { text: Sanity.Schema.RichContent };
  preview?: boolean;
}

const Wrapper = styled(Box)`
  --height: 400px;

  margin-top: var(--space-2);

  @media screen and (min-width: ${breakpoints.large}) {
    --height: auto;

    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  @media screen and (min-width: ${breakpoints.xLarge}) {
    grid-template-columns: 1.5fr 1fr;
  }
`;

const Content = styled(Box)`
  --padding: var(--space-2);

  @media screen and (min-width: ${breakpoints.large}) {
    --padding: var(--space-4);
  }

  @media screen and (min-width: ${breakpoints.xLarge}) {
    --padding: var(--space-8);
  }
`;

const Address = styled.address`
  font-style: normal;
  line-height: var(--line-height-x-large);
  white-space: pre;
`;

const LocationBadge = styled.span`
  position: relative;
  top: -1px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: var(--color-white);
  font-size: 50%;
  font-family: var(--font-heading);
  vertical-align: middle;
  background-color: var(--color-black);
  border-radius: 100%;
`;

const LocationsPage: NextPage<LocationsPageProps> = ({
  sharedData,
  locationsData,
  locationsTextData,
  preview,
}) => {
  const { data: locations } = usePreviewSubscription(locationsQuery, {
    initialData: locationsData,
    enabled: preview,
  });

  const { data: shared } = usePreviewSubscription(sharedDataQuery, {
    initialData: sharedData,
    enabled: preview,
  });

  const { data: locationsText } = usePreviewSubscription(locationsTextQuery, {
    initialData: locationsTextData,
    enabled: preview,
  });

  const locationsWithoutPrimary = locations.filter(
    (location) => !location.primary
  );

  return (
    <Layout preview={preview} spaceLinks={shared.spaceInfoPaths}>
      <SEO title='Locations' />
      <Hero
        title='Our locations'
        description='Our Salon Studio hubs are placed in high-end retail locations to help your business thrive.'
        backgroundColor='pink'
        flourishTopLeft='small'
        flourishBottomRight='small'
        cta={
          <Link href={'./contact'} passHref>
            <Button as='a'>{'Book a tour'}</Button>
          </Link>
        }
      />
      <Wrapper>
        {/* If there is more than one location, use the helper function to generate the map bounds
          otherwise, center the map on the sole location and set an appropriate zoom - defaults
          to [0,0] (London), when there are no locations*/}
        <Map
          {...(locationsWithoutPrimary.length < 2
            ? {
                zoom: [14],
                center: [
                  locationsWithoutPrimary[0].mapLocation?.lng || 0,
                  locationsWithoutPrimary[0].mapLocation?.lat || 0,
                ],
              }
            : { mapBounds: getBoundsFromLocations(locationsWithoutPrimary) })}
        >
          {locationsWithoutPrimary?.map((location, index) => (
            <MapMarker
              number={index + 1}
              key={location._id}
              coordinates={[
                location.mapLocation?.lng || 0,
                location.mapLocation?.lat || 0,
              ]}
            />
          ))}
        </Map>
        <Content>
          <Stack space={5}>
            {locationsWithoutPrimary.map((location, index) => (
              <div key={location._id}>
                <Stack space={2}>
                  <Stack space={0}>
                    <Text as='strong' fontWeight='bold' fontSize='small'>
                      <LocationBadge>{index + 1}</LocationBadge> {location.name}
                    </Text>
                    <Address>{formatAddress(location)}</Address>
                  </Stack>
                  <ButtonGroup>
                    {/* <Link href={SETTINGS.routes.services.slug} passHref>
                      <Button as='a'>Find service</Button>
                    </Link> */}
                    <Button
                      variant='outline'
                      as='a'
                      href={`https://www.google.com/maps/dir//${formatAddressWeblink(
                        location
                      )}`}
                    >
                      Get directions
                    </Button>
                  </ButtonGroup>
                </Stack>
              </div>
            ))}

            {locationsText && <BlockContent blocks={locationsText.text} />}
          </Stack>
        </Content>
      </Wrapper>
    </Layout>
  );
};

export default LocationsPage;

export const getStaticProps: GetStaticProps<LocationsPageProps> = async ({
  preview = __DEV__,
}) => {
  const sharedData = await getClient(preview).fetch<SharedData>(
    sharedDataQuery
  );

  const locationsData = await getClient(preview).fetch<
    Sanity.Schema.Location[]
  >(locationsQuery);

  const locationsTextData = await getClient(preview).fetch<any>(
    locationsTextQuery
  );

  return {
    props: {
      sharedData,
      locationsData,
      locationsTextData,
      preview,
    },
    revalidate: REVALIDATE_TIME,
  };
};
